import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  IconCompose,
  IconMail,
  IconSend,
  Stack,
  Text,
} from 'braid-design-system';
import type { FC } from 'react';

import DateFormat from 'src/components/common/DateFormat';
import HotjarMask from 'src/components/common/HotjarMask';
import HtmlContent from 'src/components/details/PreviousApplications/Details/HtmlContent';
import { APPLIED } from 'src/constants/sourceId';

import translations from '../.vocab';
import { AvatarGroup } from '../AvatarGroup/AvatarGroup';
import { TimelineItem } from '../TimelineItem';
import type { Email, Note } from '../types';

interface ApplicationTimelineProps {
  id: string;
  emailList: Email[];
  noteList: Note[];
  appliedDate: string;
  source: number;
  locale: Locale;
}

export const ApplicationTimeline: FC<ApplicationTimelineProps> = ({
  id,
  emailList,
  noteList,
  appliedDate,
  source,
  locale,
}) => {
  const { t } = useTranslations(translations);

  const hirers: string[] = [];

  if (emailList || noteList) {
    emailList?.forEach((email) => {
      if (email.from) {
        hirers.push(email.from);
      }
    });

    noteList?.forEach((note) => {
      if (note.createdUser) {
        hirers.push(note.createdUser);
      }
    });
  }

  const sortedHirers = hirers
    ? [...new Set(hirers)].sort((a, b) => a.localeCompare(b))
    : [];

  const timelineItems: Array<
    | {
        type: 'email';
        date: string;
        object: Email;
      }
    | {
        type: 'note';
        date: string;
        object: Note;
      }
  > = [];

  if (emailList) {
    emailList.map((email) => {
      timelineItems.push({
        type: 'email',
        date: email.sentDate ? email.sentDate : '',
        object: email,
      });
    });
  }

  if (noteList) {
    noteList.map((note) => {
      timelineItems.push({
        type: 'note',
        date: note.createdDate ? note.createdDate : '',
        object: note,
      });
    });
  }

  const sortTimelineItems = timelineItems
    ? timelineItems.sort((a, b) => b.date.localeCompare(a.date))
    : [];

  return (
    <Stack space="small" align="left">
      {hirers.length > 0 ? (
        <AvatarGroup id={id} names={sortedHirers} size="xsmall" space="small" />
      ) : null}
      <Stack space="xxsmall">
        {sortTimelineItems.map((item) => {
          if (item.type === 'email') {
            const email = item.object;
            return (
              <TimelineItem
                key={item.object.id}
                icon={
                  <Box style={{ marginTop: '-5px' }}>
                    <IconMail tone="secondary" size="xsmall" />
                  </Box>
                }
                date={<DateFormat date={item.date} locale={locale} />}
                content={
                  <>
                    <Text size="small">
                      <HotjarMask>
                        {t('From: name', {
                          name: email.from ? email.from : '',
                        })}
                      </HotjarMask>
                    </Text>
                    <Text size="small">
                      <HotjarMask>
                        {t('To: name', { name: email.to ? email.to : '' })}
                      </HotjarMask>
                    </Text>
                    <Text size="small">
                      <HotjarMask>
                        {t('Subject: subject', {
                          subject: email.subject ? email.subject : '',
                        })}
                      </HotjarMask>
                    </Text>
                    <HtmlContent
                      content={email.message}
                      size="small"
                      space="xsmall"
                      isSecondary={false}
                    />
                  </>
                }
              />
            );
          }

          if (item.type === 'note') {
            const note = item.object;
            return (
              <TimelineItem
                key={item.object.id}
                icon={
                  <Box style={{ marginTop: '-5px' }}>
                    <IconCompose tone="secondary" size="xsmall" />
                  </Box>
                }
                date={<DateFormat date={item.date} locale={locale} />}
                content={
                  <>
                    <Text size="small">
                      <HotjarMask>
                        {t('From: name', {
                          name: note.createdUser ? note.createdUser : '',
                        })}
                      </HotjarMask>
                    </Text>
                    <HtmlContent
                      content={note.content}
                      size="small"
                      space="xsmall"
                      isSecondary={false}
                    />
                  </>
                }
              />
            );
          }
        })}

        <TimelineItem
          icon={
            <Box style={{ marginTop: '-5px' }}>
              <IconSend tone="secondary" size="xsmall" />
            </Box>
          }
          date={<DateFormat date={appliedDate} locale={locale} />}
          content={
            <Text size="small">
              {source === APPLIED ? t('Applied via Seek') : t('Manually added')}
            </Text>
          }
          isLast={true}
        />
      </Stack>
    </Stack>
  );
};
